import Image from "next/image";
import clsx from "clsx";

import {
  openModal,
  PickAndMixQuery,
  selectIsEditModeOnForWeek,
} from "@chef/state-management";
import { getCurrentWeekDataAndBasketFromCalendar } from "@chef/state-management/helpers";
import { RecipeReactionButton, RecipeTags } from "@chef/smart-components";
import { findFeaturedPortraitImage } from "@chef/state-management/helpers";
import { isAllergenViolation, slugify } from "@chef/helpers";
import { MealCard } from "@chef/components";
import {
  useAppDispatch,
  useAppSelector,
  useCalendarData,
} from "@chef/state-management/hooks";
import { isEqualStrings } from "@chef/utils/equal";
import { BASE_64_1X1_GREY, language } from "@chef/constants";

import type { WeekYear } from "@chef/types/common";

import UpsaleCard from "../UpsaleCard";

import { intl } from "./WeekOverviewExperimental.Intl";

export type MyMenuCardProps = WeekYear & {
  deliveryStatus: "freezed" | "paused" | "active";
} & (
    | {
        type: "upsale";
        img: string;
        recipe?: never;
        portions?: never;
        handleRemoveRecipe?: never;
      }
    | {
        type: "recipe";
        img?: never;
        recipe: PickAndMixQuery["pickAndMix"][number]["recipes"][number];
        portions: string;
        handleRemoveRecipe: () => void;
      }
  );

export const MyMenuCard = ({
  handleRemoveRecipe,
  img,
  recipe,
  portions,
  type,
  week,
  year,
}: MyMenuCardProps) => {
  const dispatch = useAppDispatch();

  const isEditModeOnForWeek = useAppSelector(
    selectIsEditModeOnForWeek({ week, year }),
  );

  const { calendar } = useCalendarData({
    week,
    year,
  });

  const currentWeekObject = getCurrentWeekDataAndBasketFromCalendar({
    calendar: calendar || [],
    week,
    year,
  });

  if (type === "upsale") {
    return <UpsaleCard imgUrl={img} scrollToId="menu-options-section" />;
  }
  const { mealboxBasket } = currentWeekObject || {};

  const { basketDetails: { products: basketProducts = [] } = {} } =
    mealboxBasket || {};

  const matchingProduct = basketProducts.find((product) =>
    recipe.portions?.some((portion) =>
      isEqualStrings(portion.variationId, product.variationId),
    ),
  );

  const dishIsNonCompliant = isAllergenViolation(
    matchingProduct?.allergenComplianceId,
  );

  const url = findFeaturedPortraitImage(recipe)?.url || BASE_64_1X1_GREY;

  return (
    <div className="relative">
      <MealCard
        className={clsx(
          "bg-white rounded shadow",
          dishIsNonCompliant && "border-2 border-error",
          "h-full",
        )}
        id={matchingProduct?.variationId}
        language={language}
        text={recipe.recipeName}
        tags={<RecipeTags recipe={recipe} />}
        href={`/${intl.RECIPE}/${recipe.recipeId}/${slugify(
          recipe.recipeName,
        )}`}
        Image={() => (
          <Image
            src={url}
            width={2}
            height={3}
            priority
            alt={recipe.recipeName}
            className="object-cover w-full h-auto rounded-t aspect-2/3"
            sizes="(max-width: 375px) 50w, (max-width: 768px) 35vw, 20vw"
          />
        )}
        onAddRemoveProduct={handleRemoveRecipe}
        onClick={(e) => {
          e?.preventDefault();

          dispatch(
            openModal({
              id: "RECIPE",
              recipeId: recipe.recipeId,
              misc: {
                portionSize: portions,
              },
              view: "deviation",
            }),
          );
        }}
        inBasket={isEditModeOnForWeek}
        hideAddRemoveProductButton={!isEditModeOnForWeek}
      />
      <RecipeReactionButton
        recipe={recipe}
        className="absolute z-10 shadow top-4 right-4"
      />
    </div>
  );
};
